import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { sortBy } from "lodash";
import {
  ArrowPathIcon as RefreshIcon,
  InformationCircleIcon,
} from "@heroicons/react/24/outline";
import { FlagIcon as FlagIconSolid } from "@heroicons/react/24/solid";
import { useInView } from "react-intersection-observer";
import { useQueryClient } from "react-query";
import ReactTooltip from "react-tooltip";

import {
  BlueprintAssignInput,
  BlueprintMinimalInfo,
  CheckTypeInfo,
  TURN_AROUND_CALCULATIONS,
} from "../api/Blueprints";
import {
  resetCheckTypesQueries,
  useBlueprintAssign,
  useInfiniteCheckTypes,
} from "../api/BlueprintsApi";
import { behaviorRelabelMapping, useFamaFlags } from "../api/FamaFlags";
import { REPORT_TYPES } from "../api/Reports";
import {
  COMPANY_ASSIGN_CHECK_TYPE_ICON_BADGE_TOOLTIP_ID,
  IconBadgeTooltipLayer,
  IconBadgeTooltipProvider,
} from "../check-types/iconBadgeTooltip";
import { slaDisplay } from "../check-types/slaDisplay";
import Button, {
  ButtonColorTypes,
  ButtonTypes,
} from "../shared-components/Button";
import CheckBox from "../shared-components/CheckBox";
import ErrorMessageContainer from "../shared-components/ErrorMessageContainer";
import LoadingSpinner from "../shared-components/LoadingSpinner";
import { Modal } from "../shared-components/Modal";
import Toggle from "../shared-components/Toggle";
import { blueprint_tooltip, sortNamedListAlpha } from "../utils";
import { CompanyAddCheckTypeTableView } from "./CompanyAddCheckTypeTableView";
import type { CompanyAssignCheckTypeButtonProps } from "./companyAssignCheckTypeButton.types";

const MIN_VISIBLE_BEFORE_AUTO_FETCH = 5;
const SKELETON_ROW_COUNT = 6;
/** Mounted outside Modal — Dialog.Panel uses overflow-hidden + transform. */
const COMPANY_ASSIGN_CHECK_TYPE_SLA_TOOLTIP_ID =
  "company-assign-check-type-sla-tooltip";

type ViewMode = "table" | "expandable";

function toReportType(reportType?: string): REPORT_TYPES {
  if (reportType === REPORT_TYPES.THREE_SIXTY) return REPORT_TYPES.THREE_SIXTY;
  return REPORT_TYPES.MISCONDUCT;
}

export function filterAvailableCheckTypes(
  checkTypes: CheckTypeInfo[] | undefined,
  existingBlueprints: string[],
  blueprintsFromParent?: string[]
): CheckTypeInfo[] {
  let available = sortBy(
    checkTypes ?? [],
    (checkType) => checkType.name?.toLocaleLowerCase() ?? ""
  ).filter(
    (checkType) =>
      !existingBlueprints.some(
        (existingId) => existingId === checkType.blueprint_uuid
      )
  );

  if ((blueprintsFromParent ?? []).length > 0) {
    available = available.filter((checkType) =>
      blueprintsFromParent?.some(
        (parentId) => parentId === checkType.blueprint_uuid
      )
    );
  }

  return available;
}

function CheckTypeRowSkeleton() {
  return (
    <div
      data-testid="check-type-row-skeleton"
      className="rounded-md border border-gray-300 p-3 mb-3"
    >
      <div className="flex items-center gap-4">
        <div className="w-4 h-4 skeleton skeleton__grey rounded" />
        <div className="h-4 w-2/3 max-w-xs skeleton skeleton__grey rounded" />
      </div>
    </div>
  );
}

function CheckTypeExpandDetails({ checkType }: { checkType: CheckTypeInfo }) {
  const { behaviorMappingActions } = useFamaFlags();
  const behaviorNames = sortNamedListAlpha(
    Array.from(new Set(checkType.behaviors ?? []))
  );
  const relabeledBehaviors = behaviorRelabelMapping(
    behaviorNames,
    behaviorMappingActions
  );
  const turnAroundDisplay = slaDisplay(
    checkType.turn_around,
    checkType.sla_hours
  );

  return (
    <div className="mt-2 text-sm border-t border-gray-200 pt-3">
      <div className="font-medium text-gray-900 mb-2">Fama Behavior Flags:</div>
      {relabeledBehaviors.length > 0 ? (
        <div className="grid grid-cols-2 gap-x-6 gap-y-2 mb-4 px-1">
          {relabeledBehaviors.map((name) => (
            <div key={name} className="flex items-center gap-2">
              <FlagIconSolid className="w-4 h-4 text-green-600 shrink-0" />
              <span className="capitalize text-gray-800">
                {name.toLowerCase()}
              </span>
            </div>
          ))}
        </div>
      ) : (
        <div className="mb-4 px-1 text-gray-500">No Assigned Behaviors</div>
      )}

      <div className="flex items-center gap-2 font-medium text-gray-900 mb-2">
        Report SLA Calculations:
        <span
          data-tip={blueprint_tooltip}
          data-for={COMPANY_ASSIGN_CHECK_TYPE_SLA_TOOLTIP_ID}
          data-testid="assign-check-type-sla-tip-trigger"
        >
          <InformationCircleIcon
            className="w-5 h-5 text-gray-400"
            aria-hidden="true"
          />
        </span>
      </div>
      {checkType.turn_around === TURN_AROUND_CALCULATIONS.Hour ? (
        <div className="mb-4 px-1 text-gray-800">
          Check Type SLA (in Hours): {checkType.sla_hours}
        </div>
      ) : (
        <div className="mb-4 px-1 text-gray-800">{turnAroundDisplay}</div>
      )}

      <div className="font-medium text-gray-900 mb-2">Lookback:</div>
      <div className="px-1 text-gray-800">
        Lookback period for blueprint (months): <b>{checkType.lookback}</b>
      </div>
    </div>
  );
}

type CheckTypeAssignRowProps = {
  checkType: CheckTypeInfo;
  selected: boolean;
  onToggleSelect: (blueprintUUID: string) => void;
};

function CheckTypeAssignRow({
  checkType,
  selected,
  onToggleSelect,
}: CheckTypeAssignRowProps) {
  return (
    <div
      data-testid="check-type-assign-row"
      className="rounded-md border border-gray-300 bg-white p-3 mb-3"
    >
      <label className="flex items-center gap-4 cursor-pointer">
        <CheckBox
          id={`assign-check-type-${checkType.blueprint_uuid}`}
          checked={selected}
          onChange={() => onToggleSelect(checkType.blueprint_uuid)}
        />
        <span className="font-bold text-gray-900">{checkType.name}</span>
      </label>
      {selected && <CheckTypeExpandDetails checkType={checkType} />}
    </div>
  );
}

export const CompanyAddCheckTypeButton = ({
  existingBlueprints,
  blueprintsFromParent,
  companyUUID,
  onSave,
  useButtonOutline = false,
}: CompanyAssignCheckTypeButtonProps) => {
  const queryClient = useQueryClient();
  const [showModal, setShowModal] = useState(false);
  const [selectedBlueprints, setSelectedBlueprints] = useState<string[]>([]);
  const [viewMode, setViewMode] = useState<ViewMode>("expandable");
  const [tableViewToggleKey, setTableViewToggleKey] = useState(0);
  const [isHardRefreshing, setIsHardRefreshing] = useState(false);
  /** Ignores stale reset finally callbacks when refresh is clicked again. */
  const hardRefreshGenerationRef = useRef(0);
  /** Snapshot at save time so onSave names survive a mid-assign cache reset. */
  const pendingAssignCheckTypesRef = useRef<Map<string, CheckTypeInfo>>(
    new Map()
  );
  /** Prevents a second onSave if success is still true when the catalog updates. */
  const handledAssignSuccessRef = useRef(false);

  const {
    isLoading: isLoadingBlueprintAssign,
    data: blueprintAssignData,
    error: errorBlueprintAssign,
    mutate: assignBlueprint,
    reset: resetBlueprintAssign,
    isSuccess: isSuccessBlueprintAssign,
  } = useBlueprintAssign();

  const {
    data,
    isLoading: isLoadingCheckTypes,
    isFetchingNextPage,
    isError: isErrorCheckTypes,
    hasNextPage,
    fetchNextPage,
  } = useInfiniteCheckTypes({ enabled: showModal });

  const loadedCheckTypes = useMemo(
    () => data?.pages.flatMap((page) => page.data) ?? [],
    [data]
  );

  const availableCheckTypes = useMemo(
    () =>
      filterAvailableCheckTypes(
        loadedCheckTypes,
        existingBlueprints,
        blueprintsFromParent
      ),
    [loadedCheckTypes, existingBlueprints, blueprintsFromParent]
  );

  const checkTypeByUUID = useMemo(() => {
    const map = new Map<string, CheckTypeInfo>();
    loadedCheckTypes.forEach((checkType) => {
      map.set(checkType.blueprint_uuid, checkType);
    });
    return map;
  }, [loadedCheckTypes]);

  // Fallback lookup without putting the Map in the success-effect deps.
  const checkTypeByUUIDRef = useRef(checkTypeByUUID);
  checkTypeByUUIDRef.current = checkTypeByUUID;

  const selectedSummary = useMemo(() => {
    return selectedBlueprints
      .map((uuid) => checkTypeByUUID.get(uuid)?.name)
      .filter((name): name is string => Boolean(name))
      .join(", ");
  }, [selectedBlueprints, checkTypeByUUID]);

  const { ref: loadMoreRef, inView } = useInView({
    rootMargin: "120px",
  });

  const close = useCallback(() => {
    setShowModal(false);
    setSelectedBlueprints([]);
    setIsHardRefreshing(false);
    pendingAssignCheckTypesRef.current = new Map();
    // Keep handledAssignSuccessRef so a late catalog update cannot re-fire onSave
    // while mutation success is still true for a tick after close/reset.
    resetBlueprintAssign();
  }, [resetBlueprintAssign]);

  const openModal = useCallback(() => {
    setSelectedBlueprints([]);
    setIsHardRefreshing(false);
    pendingAssignCheckTypesRef.current = new Map();
    handledAssignSuccessRef.current = false;
    setViewMode("expandable");
    setTableViewToggleKey((key) => key + 1);
    setShowModal(true);
  }, []);

  const handleRefreshCheckTypes = useCallback(() => {
    if (isLoadingBlueprintAssign) {
      return;
    }
    setSelectedBlueprints([]);
    const generation = ++hardRefreshGenerationRef.current;
    setIsHardRefreshing(true);
    // Await resetQueries so skeletons clear even when no isLoading/isFetching
    // flip is visible to an effect (the previous ref-gated approach could stick).
    void Promise.resolve(resetCheckTypesQueries(queryClient)).finally(() => {
      if (hardRefreshGenerationRef.current === generation) {
        setIsHardRefreshing(false);
      }
    });
  }, [isLoadingBlueprintAssign, queryClient]);

  useEffect(() => {
    if (!isSuccessBlueprintAssign || !blueprintAssignData) {
      return;
    }
    if (handledAssignSuccessRef.current) {
      return;
    }
    handledAssignSuccessRef.current = true;

    const assignLookup = pendingAssignCheckTypesRef.current;
    const blueprintList: BlueprintMinimalInfo[] = blueprintAssignData.map(
      (assignment) => {
        const foundCheckType =
          assignLookup.get(assignment.blueprint_uuid) ??
          checkTypeByUUIDRef.current.get(assignment.blueprint_uuid);
        return {
          name: foundCheckType?.name ?? "",
          blueprint_uuid:
            foundCheckType?.blueprint_uuid ?? assignment.blueprint_uuid,
          report_type: toReportType(foundCheckType?.report_type),
        };
      }
    );
    pendingAssignCheckTypesRef.current = new Map();
    onSave(blueprintList);
    close();
  }, [isSuccessBlueprintAssign, blueprintAssignData, onSave, close]);

  // Re-bind SLA help tips when expandable rows mount/unmount inside the modal.
  useEffect(() => {
    if (!showModal || viewMode !== "expandable") {
      return;
    }
    ReactTooltip.rebuild();
  }, [showModal, viewMode, selectedBlueprints]);

  // Expandable mode: one effect for sentinel + thin-list auto-fetch so both
  // triggers cannot request the same offset in the same tick.
  useEffect(() => {
    if (
      !showModal ||
      viewMode !== "expandable" ||
      !hasNextPage ||
      isFetchingNextPage ||
      isLoadingCheckTypes ||
      isErrorCheckTypes
    ) {
      return;
    }
    const shouldFetchForSentinel = inView;
    const shouldFetchForThinList =
      availableCheckTypes.length < MIN_VISIBLE_BEFORE_AUTO_FETCH;
    if (shouldFetchForSentinel || shouldFetchForThinList) {
      fetchNextPage();
    }
  }, [
    showModal,
    viewMode,
    inView,
    hasNextPage,
    isFetchingNextPage,
    isLoadingCheckTypes,
    isErrorCheckTypes,
    availableCheckTypes.length,
    fetchNextPage,
  ]);

  // Table mode: load remaining pages so in-memory filters/pagination see the full set.
  useEffect(() => {
    if (
      !showModal ||
      viewMode !== "table" ||
      !hasNextPage ||
      isFetchingNextPage ||
      isLoadingCheckTypes ||
      isErrorCheckTypes
    ) {
      return;
    }
    fetchNextPage();
  }, [
    showModal,
    viewMode,
    hasNextPage,
    isFetchingNextPage,
    isLoadingCheckTypes,
    isErrorCheckTypes,
    fetchNextPage,
  ]);

  function toggleBlueprintSelection(blueprintUUID: string) {
    setSelectedBlueprints((current) =>
      current.some((id) => id === blueprintUUID)
        ? current.filter((id) => id !== blueprintUUID)
        : [...current, blueprintUUID]
    );
  }

  function handleSave() {
    const assignLookup = new Map<string, CheckTypeInfo>();
    selectedBlueprints.forEach((blueprintUUID) => {
      const checkType = checkTypeByUUID.get(blueprintUUID);
      if (checkType) {
        assignLookup.set(blueprintUUID, checkType);
      }
    });
    pendingAssignCheckTypesRef.current = assignLookup;

    const assign: BlueprintAssignInput[] = selectedBlueprints.map(
      (blueprintUUID) => ({
        blueprint_uuid: blueprintUUID,
        company_uuid: companyUUID,
      })
    );
    assignBlueprint(assign);
  }

  const showInitialSkeleton =
    isHardRefreshing || (isLoadingCheckTypes && loadedCheckTypes.length === 0);
  const showEmptyState =
    !showInitialSkeleton &&
    !isErrorCheckTypes &&
    availableCheckTypes.length === 0 &&
    !hasNextPage &&
    !isFetchingNextPage;

  const modalWidth =
    viewMode === "table" ? "w-full max-w-7xl p-3" : "w-full max-w-3xl p-3";

  return (
    <div>
      <Button
        text="Assign Check Type"
        type={ButtonTypes.add}
        onClick={openModal}
        colorType={
          useButtonOutline ? ButtonColorTypes.outline : ButtonColorTypes.primary
        }
      />
      <Modal open={showModal} onClose={close} width={modalWidth}>
        <IconBadgeTooltipProvider
          tooltipId={COMPANY_ASSIGN_CHECK_TYPE_ICON_BADGE_TOOLTIP_ID}
        >
          <div
            className={
              viewMode === "table"
                ? "flex flex-col max-h-[min(90vh,56rem)]"
                : "flex flex-col max-h-[min(80vh,40rem)]"
            }
          >
            <div className="flex items-start justify-between gap-4 mb-4 shrink-0">
              <div className="text-2xl font-bold">Add Check Types</div>
              <div className="flex items-center gap-3 shrink-0 pt-1">
                <div className="[&>div]:w-auto [&>div]:mr-0">
                  <Toggle
                    key={tableViewToggleKey}
                    label="Table view"
                    labelClassName="text-sm font-medium text-gray-700 mr-3 w-auto"
                    defaultValue={false}
                    onChange={(enabled) =>
                      setViewMode(enabled ? "table" : "expandable")
                    }
                  />
                </div>
                <button
                  type="button"
                  aria-label="Refresh check types"
                  title="Refresh check types"
                  className="text-green-600 disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={isLoadingBlueprintAssign}
                  onClick={handleRefreshCheckTypes}
                >
                  <RefreshIcon
                    className="w-5 cursor-pointer"
                    aria-hidden="true"
                  />
                </button>
              </div>
            </div>

            {viewMode === "table" ? (
              <div className="flex-1 min-h-0 overflow-y-auto">
                <CompanyAddCheckTypeTableView
                  checkTypes={isHardRefreshing ? [] : availableCheckTypes}
                  selectedBlueprints={selectedBlueprints}
                  onToggleSelect={toggleBlueprintSelection}
                  isLoading={showInitialSkeleton}
                  isError={isErrorCheckTypes}
                  isLoadingMore={
                    !isHardRefreshing &&
                    !isErrorCheckTypes &&
                    Boolean(hasNextPage || isFetchingNextPage)
                  }
                />
              </div>
            ) : isErrorCheckTypes &&
              availableCheckTypes.length === 0 &&
              !isHardRefreshing ? (
              <ErrorMessageContainer message="Cannot get check type information, please contact support" />
            ) : (
              <div className="flex-1 min-h-[16rem] border border-gray-300 rounded-md overflow-y-auto p-3">
                {showInitialSkeleton &&
                  Array.from({ length: SKELETON_ROW_COUNT }).map((_, index) => (
                    <CheckTypeRowSkeleton key={`skeleton-${index}`} />
                  ))}

                {!showInitialSkeleton &&
                  availableCheckTypes.map((checkType) => (
                    <CheckTypeAssignRow
                      key={checkType.blueprint_uuid}
                      checkType={checkType}
                      selected={selectedBlueprints.includes(
                        checkType.blueprint_uuid
                      )}
                      onToggleSelect={toggleBlueprintSelection}
                    />
                  ))}

                {showEmptyState && (
                  <div className="p-6 text-center text-gray-500 text-sm">
                    No check types available to assign
                  </div>
                )}

                {!isHardRefreshing && isFetchingNextPage && (
                  <>
                    <CheckTypeRowSkeleton />
                    <CheckTypeRowSkeleton />
                  </>
                )}

                <div ref={loadMoreRef} className="h-1" aria-hidden="true" />
              </div>
            )}

            <div className="shrink-0 mt-4">
              <div className="text-sm text-gray-800 mb-4 min-h-[1.25rem]">
                {selectedBlueprints.length > 0
                  ? `Selected: ${selectedSummary}`
                  : "\u00a0"}
              </div>

              {errorBlueprintAssign && (
                <ErrorMessageContainer message="Error assigning blueprints please contact the administrator" />
              )}

              <div className="flex justify-end gap-3">
                <Button
                  text="Cancel"
                  colorType={ButtonColorTypes.cancel}
                  onClick={close}
                />
                {!errorBlueprintAssign && !isLoadingBlueprintAssign && (
                  <Button
                    text="Save"
                    disabled={
                      selectedBlueprints.length === 0 || isLoadingCheckTypes
                    }
                    onClick={handleSave}
                  />
                )}
                {isLoadingBlueprintAssign && <LoadingSpinner />}
              </div>
            </div>
          </div>
        </IconBadgeTooltipProvider>
      </Modal>
      {showModal && viewMode === "table" && (
        <IconBadgeTooltipLayer
          id={COMPANY_ASSIGN_CHECK_TYPE_ICON_BADGE_TOOLTIP_ID}
        />
      )}
      {showModal && viewMode === "expandable" && (
        <div data-testid="assign-check-type-sla-tooltip-layer">
          <ReactTooltip
            id={COMPANY_ASSIGN_CHECK_TYPE_SLA_TOOLTIP_ID}
            className="hover:visible:opacity-1 z-[10000] w-96"
            place="right"
            delayHide={1000}
            effect="solid"
            clickable={true}
            html={true}
          />
        </div>
      )}
    </div>
  );
};
